import type { FC } from 'react';
import React, { memo, useEffect, Fragment, useMemo } from 'react';

import { IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { IN_EDITOR_TEMPLATES_METRIC } from '@confluence/template-utils/entry-points/IN_EDITOR_TEMPLATES_METRIC';

import { usePanelOpenAnalyticsEvents } from './usePanelOpenAnalyticsEvents';
import { useLoadTemplatePanelExperienceTracker } from './useLoadTemplatePanelExperienceTracker';
import { ContentCreationPanelTabs } from './ContentCreationPanelTabs';
import { collectLoadPanelExperiences, filterSubExperiences } from './experienceTrackerHelpers';

type Props = {
	hasUserAddedContent: boolean | null;
	isPanelDisabled: boolean;
	title: string;
	onContentUpdated: () => void;
	beforeTemplateSelect?: () => void;
	beforeUpdateRequest?: () => void;
	isContentCreationPanelShown: boolean;
	onClickCloseContentCreationPanel: () => void;
	onError?: () => void;
	templateAllowList?: string[];
	isImportDisabled?: boolean;
	isAlwaysDiscardChanges?: boolean;
	isSpacePickerDisabled?: boolean;
};

export const EditorContextPanelContentCreationPanel: FC<Props> = memo(
	({
		hasUserAddedContent,
		isPanelDisabled,
		title,
		onContentUpdated,
		beforeTemplateSelect,
		isContentCreationPanelShown,
		onClickCloseContentCreationPanel,
		beforeUpdateRequest,
		onError,
		templateAllowList,
		isImportDisabled,
		isAlwaysDiscardChanges,
		isSpacePickerDisabled,
	}) => {
		const [currentSpaceKey] = usePageSpaceKey();
		const [contentId] = usePageContentId();
		usePanelOpenAnalyticsEvents(isContentCreationPanelShown, contentId);
		const { abortTemplatePanelLoadExperiences } = useLoadTemplatePanelExperienceTracker();

		const subExperiences = useMemo(
			() => filterSubExperiences(isSpacePickerDisabled),
			[isSpacePickerDisabled],
		);

		useEffect(() => {
			if (!isContentCreationPanelShown) {
				abortTemplatePanelLoadExperiences('closing template panel');
			}
		}, [isContentCreationPanelShown, abortTemplatePanelLoadExperiences]);
		if (isContentCreationPanelShown && currentSpaceKey && contentId) {
			return (
				<Fragment>
					<ExperienceStart
						name={IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE}
						collect={collectLoadPanelExperiences(subExperiences)}
						timeout={ExperienceTimeout.IN_EDITOR_TEMPLATE_PANEL_LOAD}
					/>
					{subExperiences.map((name) => (
						<ExperienceStart name={name} key={name} />
					))}
					<PageSegmentLoadStart metric={IN_EDITOR_TEMPLATES_METRIC} />
					<ContentCreationPanelTabs
						currentSpaceKey={currentSpaceKey!}
						contentId={contentId!}
						title={title}
						onContentUpdated={onContentUpdated}
						isPanelDisabled={isPanelDisabled}
						beforeTemplateSelect={beforeTemplateSelect}
						hasUserAddedContent={hasUserAddedContent}
						beforeUpdateRequest={beforeUpdateRequest}
						onError={onError}
						closeConfigPanel={onClickCloseContentCreationPanel}
						abortTemplatePanelLoadExperiences={abortTemplatePanelLoadExperiences}
						templateAllowList={templateAllowList}
						isImportDisabled={isImportDisabled}
						isAlwaysDiscardChanges={isAlwaysDiscardChanges}
						isSpacePickerDisabled={isSpacePickerDisabled}
					/>
				</Fragment>
			);
		}

		return null;
	},
);
